import { useCallback, useEffect, useRef, useState } from 'react';

import type {
  SoftphoneCallResult,
  SoftphoneCarrierFailure,
  SoftphoneClient,
  SoftphoneDiagnostic,
  SoftphoneStatus,
} from './softphoneClient.types';

/**
 * How long, and how often, to ask the server how the carrier settled a call that ended without
 * ever connecting. The status callback usually lands within a second of the disconnect; ~6s covers
 * a slow webhook without leaving the rep staring at a spinner-less "Call ended".
 */
const CALL_RESULT_POLL_ATTEMPTS = 8;
const CALL_RESULT_POLL_INTERVAL_MS = 750;

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Thrown by `startServerCall` when the server declined to START the call for a benign reason the
 * surface has already handled (e.g. a teammate is already dialing this queued call, so the page has
 * shown a toast and advanced to the next contact). `start()` treats it as a quiet cancel — reset to
 * idle, NOT the error state — so the next contact's dialer isn't left showing a stale error.
 */
export class CallAbortedError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'CallAbortedError';
  }
}

export interface UseSoftphoneParams {
  /** Provider-specific client, created by the surface (dashboard / extension) so the mic strategy is injected there. */
  client: SoftphoneClient;
  /** Mint a fresh browser token (tRPC `voiceCall.getClientToken`). */
  fetchToken: () => Promise<{ token: string }>;
  /** Create the server-side pending call and resolve the number to dial (tRPC `voiceCall.startCall`). */
  startServerCall: () => Promise<{
    voiceCallId: string;
    destinationNumber: string;
    callerNumber: string | null;
    recordingEnabled?: boolean;
  }>;
  /**
   * Read how the carrier settled a call (tRPC `voiceCall.getCallResult`). Optional: when
   * provided, every ended call is followed up with a short poll so a carrier-refused dial
   * (`callStatus === 'failed'`) surfaces as `carrierFailure` instead of masquerading as
   * "Call ended". Surfaces without it never learn the difference.
   */
  fetchCallResult?: (voiceCallId: string) => Promise<SoftphoneCallResult>;
  /**
   * Optional sink for carrier diagnostics (see `SoftphoneDiagnostic`) — the surface decides where
   * they go, so the dashboard can report to Sentry while this package stays reporter-free.
   *
   * Complements `fetchCallResult`: that one tells the REP what the server recorded, this one tells
   * ENGINEERING what the browser SDK saw, including the failures that never reach a status
   * callback for the poll to find.
   */
  onDiagnostic?: (diagnostic: SoftphoneDiagnostic) => void;
}

export interface SoftphoneController {
  status: SoftphoneStatus;
  durationSec: number;
  isMuted: boolean;
  error: string | null;
  isRecording: boolean;
  voiceCallId: string | null;
  /**
   * Set when the just-ended call was refused by the carrier (see `SoftphoneCarrierFailure`).
   * Null while the call is live, while the post-call poll is still running, for a call that
   * connected or simply wasn't answered, and whenever the surface didn't supply `fetchCallResult`.
   */
  carrierFailure: SoftphoneCarrierFailure | null;
  /**
   * The from-number of the in-flight (or just-finished) call, from the startCall response.
   * The server pins a contact's number on first dial, so this is the only place a first-time
   * contact's assigned number is known client-side. Null until startServerCall resolves.
   */
  callerNumber: string | null;
  start: () => Promise<void>;
  hangup: () => void;
  /** Hang up the active call (if any) and reset all call state to idle. Used when the dialer closes. */
  close: () => void;
  toggleMute: () => void;
  /** Send DTMF tones on the live call (keypad / IVR navigation). */
  sendDigits: (digits: string) => void;
}

/** Owns a SoftphoneClient's lifecycle and exposes call state to a presentational component. */
export function useSoftphone({
  client,
  fetchToken,
  startServerCall,
  fetchCallResult,
  onDiagnostic,
}: UseSoftphoneParams): SoftphoneController {
  const [status, setStatus] = useState<SoftphoneStatus>('idle');
  const [durationSec, setDurationSec] = useState(0);
  const [isMuted, setIsMuted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isRecording, setIsRecording] = useState(false);
  const [voiceCallId, setVoiceCallId] = useState<string | null>(null);
  const [callerNumber, setCallerNumber] = useState<string | null>(null);
  const [carrierFailure, setCarrierFailure] = useState<SoftphoneCarrierFailure | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // Synchronous re-entry guard for start(). `status` can't be used here — two clicks in the same
  // tick both read the pre-`setStatus` value, so the guard must flip before the first `await`.
  const startInFlightRef = useRef(false);

  // Held in a ref so a caller passing an inline `onDiagnostic` can't re-register the client's
  // handlers (and tear the device down) on every render.
  const onDiagnosticRef = useRef(onDiagnostic);
  onDiagnosticRef.current = onDiagnostic;

  // The client's handlers are registered once per client (effect below), so everything they need
  // to follow up an ended call is read through refs: which row to ask about, and the surface's
  // fetcher (its identity can change per render).
  const voiceCallIdRef = useRef<string | null>(null);
  const fetchCallResultRef = useRef(fetchCallResult);
  fetchCallResultRef.current = fetchCallResult;
  // Bumped whenever a poll must be abandoned (new call, close, unmount) so a late response from a
  // previous call can't paint its failure onto the next one.
  const pollSeqRef = useRef(0);

  // Follow up EVERY ended call: ask the server how the carrier settled it. A refused `<Dial>` leg
  // reaches the browser as a plain disconnect, indistinguishable from a ring-out, so this poll is
  // the only way "the carrier blocked this call" can reach the rep. It can't be gated on "the call
  // never went active": Twilio answers the browser leg (the SDK's `accept` → `active`) as soon as
  // it starts executing our TwiML, BEFORE `<Dial>` runs, so a blocked bridge still looks like an
  // answered-then-hung-up call from here. A settled non-failed verdict costs one request.
  const pollCallResult = useCallback(async (callId: string) => {
    const fetcher = fetchCallResultRef.current;
    if (!fetcher) return;
    const seq = ++pollSeqRef.current;
    for (let attempt = 0; attempt < CALL_RESULT_POLL_ATTEMPTS; attempt++) {
      if (attempt > 0) await sleep(CALL_RESULT_POLL_INTERVAL_MS);
      if (pollSeqRef.current !== seq) return;
      let result: SoftphoneCallResult;
      try {
        result = await fetcher(callId);
      } catch {
        // Transient — the next attempt may succeed; a run of failures just leaves "Call ended".
        continue;
      }
      if (pollSeqRef.current !== seq) return;
      if (result.callStatus == null) continue;
      if (result.callStatus === 'failed') setCarrierFailure({ code: result.carrierErrorCode });
      return;
    }
  }, []);

  useEffect(() => {
    client.on({
      onStatus: (s) => {
        setStatus(s);
        const callId = voiceCallIdRef.current;
        if (s === 'ended' && callId != null) void pollCallResult(callId);
      },
      onError: (message) => {
        setError(message);
        setStatus('error');
      },
      onDiagnostic: (diagnostic) => onDiagnosticRef.current?.(diagnostic),
    });
    return () => {
      pollSeqRef.current++;
      void client.destroy();
    };
  }, [client, pollCallResult]);

  // Tick the call timer only while the call is connected.
  useEffect(() => {
    if (status === 'active' && !timerRef.current) {
      timerRef.current = setInterval(() => setDurationSec((d) => d + 1), 1000);
    }
    if (status !== 'active' && timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [status]);

  const start = useCallback(async () => {
    // Ignore a second start while one is already running (e.g. a fast double-click before the
    // dialer's backdrop mounts): two concurrent inits would race into two Twilio Devices.
    if (startInFlightRef.current) return;
    startInFlightRef.current = true;
    pollSeqRef.current++;
    voiceCallIdRef.current = null;
    setError(null);
    setDurationSec(0);
    setIsMuted(false);
    setIsRecording(false);
    setVoiceCallId(null);
    setCallerNumber(null);
    setCarrierFailure(null);
    setStatus('connecting');
    try {
      const { token } = await fetchToken();
      await client.init(token);
      const server = await startServerCall();
      voiceCallIdRef.current = server.voiceCallId;
      setVoiceCallId(server.voiceCallId);
      setIsRecording(server.recordingEnabled ?? false);
      setCallerNumber(server.callerNumber);
      await client.dial({
        destinationNumber: server.destinationNumber,
        callerNumber: server.callerNumber,
        voiceCallId: server.voiceCallId,
      });
    } catch (e) {
      if (e instanceof CallAbortedError) {
        // Benign: the surface already handled it (toast + advanced). Reset quietly to idle so the
        // NEXT contact's dialer starts clean instead of inheriting this call's error state.
        setStatus('idle');
      } else {
        setError(e instanceof Error ? e.message : 'Failed to start call');
        setStatus('error');
      }
    } finally {
      startInFlightRef.current = false;
    }
  }, [client, fetchToken, startServerCall]);

  const hangup = useCallback(() => {
    client.hangup();
  }, [client]);

  // Closing the dialer: end any live call (covers the outcome-screen path where the underlying
  // call can still be connected) AND return the controller to idle, so the next call starts
  // clean instead of inheriting the prior call's `ended`/`error` status and stale voiceCallId.
  const close = useCallback(() => {
    client.closeCall();
    pollSeqRef.current++;
    voiceCallIdRef.current = null;
    setStatus('idle');
    setError(null);
    setDurationSec(0);
    setIsMuted(false);
    setIsRecording(false);
    setVoiceCallId(null);
    setCallerNumber(null);
    setCarrierFailure(null);
  }, [client]);

  const toggleMute = useCallback(() => {
    const next = !isMuted;
    setIsMuted(next);
    // Run the side effect OUTSIDE the state updater (updaters must be pure) and guard it: a
    // provider whose mute() throws must not bubble an exception through the render/state path,
    // which would unmount the dialer and trigger its cleanup `client.destroy()` → disconnect —
    // i.e. muting would silently hang up the live call. Surface it as an error instead.
    try {
      client.setMuted(next);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to toggle mute');
    }
  }, [client, isMuted]);

  const sendDigits = useCallback(
    (digits: string) => {
      // Same guard rationale as toggleMute: Twilio's sendDigits throws on invalid characters or
      // before media is up, and an exception through the render path would unmount the dialer and
      // destroy the client — silently dropping the live call. Surface it as an error instead.
      try {
        client.sendDigits(digits);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Failed to send digits');
      }
    },
    [client],
  );

  return {
    status,
    durationSec,
    isMuted,
    error,
    isRecording,
    voiceCallId,
    carrierFailure,
    callerNumber,
    start,
    hangup,
    close,
    toggleMute,
    sendDigits,
  };
}
